import { Code, Handshake, Package } from "lucide-react";
import { NightDecor } from "@/components/blocks/decor/Decor";
import { ExternalLinkButton } from "@/components/blocks/ExternalLinkButton";
import { BlueprintScene } from "@/components/blocks/scenes/BlueprintScene";
import { CardGrid, toCardItems } from "@/components/blocks/sections/CardGrid";
import { Cta } from "@/components/blocks/sections/Cta";
import { PageHero } from "@/components/blocks/sections/PageHero";
import { Section } from "@/components/blocks/sections/Section";
import {
	displayH2,
	Overline,
	SectionHeading,
} from "@/components/blocks/sections/SectionHeading";
import { StickyStory } from "@/components/blocks/sections/StickyStory";
import { Reveal, RevealGroup, RevealItem } from "@/components/motion/motion";
import { CONTACT_EMAIL, CONTACT_URL, FOUNDER_URL } from "@/lib/constants";
import { useI18n } from "@/lib/i18n/use-i18n";

const ACTIVITY_ICONS = {
	services: Code,
	products: Package,
	support: Handshake,
} as const;

const SECTION_IDS = { founder: "fondateur", cta: "contact" } as const;

export function AboutPage() {
	const { dict } = useI18n();
	const page = dict.about;
	const process = dict.home.process;

	return (
		<>
			<PageHero
				label={page.hero.label}
				title={page.hero.title}
				emphasis={page.hero.emphasis}
				intro={page.hero.intro}
			/>

			<Section>
				<SectionHeading
					index="01"
					label={page.activity.label}
					title={page.activity.title}
				/>
				<CardGrid
					className="mt-14"
					items={toCardItems(page.activity.items, { icons: ACTIVITY_ICONS })}
				/>
			</Section>

			<Section tone="stone">
				<SectionHeading
					index="02"
					label={page.method.label}
					title={page.method.title}
					description={process.description}
				/>
				<StickyStory
					className="mt-16 md:mt-24"
					steps={Object.entries(process.steps).map(([key, step]) => ({
						key,
						title: step.title,
						description: step.description,
						details: [{ label: dict.common.yourRole, text: step.client }],
					}))}
					renderVisual={(index) => <BlueprintScene stage={index} />}
				/>
			</Section>

			<Section>
				<SectionHeading
					index="03"
					label={page.principles.label}
					title={page.principles.title}
				/>
				<RevealGroup as="ol" className="mt-14 border-t">
					{Object.entries(page.principles.items).map(([key, item], index) => (
						<RevealItem
							as="li"
							key={key}
							className="group grid gap-3 border-b py-8 md:grid-cols-[5rem_minmax(0,1fr)_minmax(0,1.2fr)] md:gap-10"
						>
							<span className="font-mono text-sm text-signal-ink">
								{String(index + 1).padStart(2, "0")}
							</span>
							<h3 className="text-xl font-semibold tracking-tight transition-transform duration-500 ease-(--ease-soft) group-hover:translate-x-1 md:text-2xl">
								{item.title}
							</h3>
							<p className="text-pretty text-muted-foreground">
								{item.description}
							</p>
						</RevealItem>
					))}
				</RevealGroup>
			</Section>

			<Section id={SECTION_IDS.founder} tone="night" decor={<NightDecor />}>
				<div className="grid items-center gap-12 md:grid-cols-[auto_minmax(0,1fr)] md:gap-16">
					<Reveal>
						<div className="relative mx-auto size-56 rounded-full border p-3 md:size-72">
							<span
								aria-hidden="true"
								className="absolute inset-1.5 rounded-full border border-dashed border-signal/50"
							/>
							<img
								src="/team/simon-boisset-288.webp"
								srcSet="/team/simon-boisset-288.webp 288w, /team/simon-boisset-576.webp 576w"
								sizes="(min-width: 768px) 288px, 224px"
								alt={page.founder.portraitAlt}
								width={288}
								height={288}
								loading="lazy"
								decoding="async"
								className="relative size-full rounded-full object-cover"
							/>
						</div>
					</Reveal>
					<Reveal delay={0.1} className="space-y-6">
						<Overline>{page.founder.label}</Overline>
						<h2 className={displayH2}>{page.founder.title}</h2>
						<p className="max-w-2xl text-lg text-pretty text-muted-foreground">
							{page.founder.description}
						</p>
						<ExternalLinkButton
							href={FOUNDER_URL}
							cta="founder"
							placement="about_founder"
							variant="outline"
						>
							{page.founder.link}
						</ExternalLinkButton>
					</Reveal>
				</div>
			</Section>

			<Section>
				<RevealGroup className="grid gap-5 md:grid-cols-2">
					<RevealItem className="rounded-3xl border bg-card p-8 md:p-10">
						<Overline>{page.practical.label}</Overline>
						<h2 className="mt-5 text-2xl font-semibold tracking-[-0.025em]">
							{page.practical.title}
						</h2>
						<dl className="mt-6 divide-y border-t">
							{Object.entries(page.practical.items).map(([key, item]) => (
								<div
									key={key}
									className="grid gap-1 py-4 sm:grid-cols-[9rem_1fr]"
								>
									<dt className="font-mono text-[11px] tracking-[0.06em] text-muted-foreground uppercase">
										{item.label}
									</dt>
									<dd className="text-pretty">{item.value}</dd>
								</div>
							))}
							<div className="grid gap-1 py-4 sm:grid-cols-[9rem_1fr]">
								<dt className="font-mono text-[11px] tracking-[0.06em] text-muted-foreground uppercase">
									{page.practical.emailLabel}
								</dt>
								<dd>
									<a
										href={CONTACT_URL}
										className="border-b border-signal/60 pb-0.5 font-medium transition-colors hover:border-signal"
									>
										{CONTACT_EMAIL}
									</a>
								</dd>
							</div>
						</dl>
					</RevealItem>
					<RevealItem className="dot-grid flex flex-col gap-4 rounded-3xl border border-dashed p-8 md:p-10">
						<h2 className="text-2xl font-semibold tracking-[-0.025em] text-balance">
							{page.staffing.title}
						</h2>
						<p className="text-pretty text-muted-foreground">
							{page.staffing.description}
						</p>
						<div className="mt-auto pt-4">
							<ExternalLinkButton
								href={FOUNDER_URL}
								cta="founder"
								placement="about_staffing"
								variant="outline"
							>
								{page.staffing.link}
							</ExternalLinkButton>
						</div>
					</RevealItem>
				</RevealGroup>
			</Section>

			<Cta id={SECTION_IDS.cta} {...page.cta} placement="about_cta" />
		</>
	);
}
